import NereusCaution from './NereusCaution';
import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import {
  Box,
  Chip,
  Fab,
  Paper,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import ChatBubble from './ChatBubble';
import NereusIcon, { NEREUS_GRADIENT, NEREUS_GRADIENT_HOVER } from './NereusIcon';
import { useChat } from '../hooks/useChat';
import { authService } from '../services/authService';

const ChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAuthenticated = authService.isAuthenticated();

  const { messages, sending, error, sendMessage } = useChat();

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  if (!isAuthenticated) return null;

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;
    setInputText('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 'calc(100px + env(safe-area-inset-bottom))', sm: 88 },
            right: { xs: 12, sm: 24 },
            left: { xs: 12, sm: 'auto' },
            width: { xs: 'auto', sm: 360 },
            height: { xs: 'min(480px, calc(100dvh - 124px))', sm: 480 },
            maxHeight: { xs: 'calc(100dvh - 124px)', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              background: NEREUS_GRADIENT,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NereusIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Nereus</Typography>
            </Box>
            <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <NereusCaution />

          {/* Messages */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', px: 1.5, py: 1.5 }}>
            {messages.length === 0 && (
              <Box sx={{ minHeight: '100%', justifyContent: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center' }}>
                  Hi! I'm Nereus, keeper of the deep. Let's chat about diving! 🤿
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['How do I improve my air consumption?', 'What should I log after each dive?', 'What did I miss in my group chats?'].map(prompt => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.7rem' }}
                      onClick={() => sendMessage(prompt)}
                      disabled={sending}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {messages.map(m => (
              <ChatBubble key={m.messageId} message={m} compact />
            ))}
            {sending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.75 }}>
                <CircularProgress size={16} sx={{ ml: 0.5 }} />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ fontSize: '0.75rem', py: 0.5 }}>{error}</Alert>}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 0.75,
              alignItems: 'flex-end',
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder="Ask a diving question…"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              InputProps={{ sx: { fontSize: { xs: '1rem', sm: '0.8rem' } } }}
            />
            <IconButton
              color="primary"
              size="small"
              onClick={handleSend}
              disabled={!inputText.trim() || sending}
              sx={{ mb: 0.25 }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      <Fab
        onClick={() => setOpen(prev => !prev)}
        sx={{
          position: 'fixed',
          bottom: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 1300,
          background: NEREUS_GRADIENT,
          color: '#FFFFFF',
          '&:hover': { background: NEREUS_GRADIENT_HOVER },
        }}
      >
        {open ? <Close /> : <NereusIcon />}
      </Fab>
    </>
  );
};

export default ChatWidget;
